import type * as React from "react";

import { Pin } from "./Pin";
import { cn } from "../lib/cn";

/*
 * The ruling, and why the numbers are what they are.
 *
 * The period is exposed as the --ruling custom property on the writing area,
 * default 1.6em. Content type is 0.9375rem (15px), so the default resolves to
 * 24px: one line of text occupies exactly one rule. Anything written on the
 * card must keep line-height equal to var(--ruling) or the text drifts off
 * the lines after a few rows. The writing area sets line-height:
 * var(--ruling) itself, and because line-height in em inherits as a computed
 * length (24px), children may change font-size (the header writes larger)
 * and still sit exactly on the ruling.
 *
 *   period       = var(--ruling) = 1.6em = 24px  (== line-height, exactly)
 *   rule stroke  = last 1px of the period
 *   background-size 100% var(--ruling)  = one period per tile, seamless
 *   background-origin content-box  = the pattern is phased off the first line
 *     box, not off the padding, so p-4 is not part of the math
 *   background-position 0 0.12em  = phase, derived below
 *
 * Phase: the rule goes in the gap between two lines of writing, not through
 * them. With Inter's metrics (ascent .969em, descent .241em, cap .727em) a
 * line box of height L puts its baseline at (L - 1.21em)/2 + .969em, so ink
 * runs from .727em above that baseline down to .241em below it. The clear band
 * between one line's descenders and the next line's capitals is therefore
 * centered L + .121em below the line box top - independent of L, because both
 * ends move with the baseline. Rounded to .12em: at 15px/24px the stroke lands
 * 4px under the descenders above it and 5px over the capitals below, so the
 * writing floats between the rules instead of being skewered by them (the
 * header, writing at 18px in the same 24px box, is the closest anything comes
 * at 2px). No rule ever crosses a glyph, so nothing has to knock the ruling
 * out behind the text - which is what lets a consumer hand the whole writing
 * area to a full-bleed button without bringing paper-colored backgrounds of
 * its own.
 *
 * Every line after the first stays put because both periods are 24px, and
 * whatever partial period is left over falls at the bottom edge - if any line
 * is off, it is the last one, like a traditional index card. The pattern also
 * tiles upwards, and that period's stroke lands a hair under the top of the
 * first line box: the card gets its top rule, above the writing, for free.
 *
 * Consequence: overriding the writing area's font-size via className rescales
 * --ruling and the phase with it (both are in em), so the whole ruling
 * follows; overriding line-height without matching var(--ruling) breaks the
 * alignment. Change both together or neither.
 *
 * --ruling-ink is the ruling's own color: --color-rule thinned, because a
 * rule a person writes over is fainter than the card's edge, and the writing
 * is what the eye should catch first.
 */
const RULING = [
  "[--ruling:1.6em]",
  "[--ruling-ink:color-mix(in_srgb,var(--color-rule)_20%,transparent)]",
  "[background-image:repeating-linear-gradient(transparent,transparent_calc(var(--ruling)_-_1px),var(--ruling-ink)_calc(var(--ruling)_-_1px),var(--ruling-ink)_var(--ruling))]",
  "[background-size:100%_var(--ruling)]",
  "[background-origin:content-box]",
  "[background-position:0_0.12em]",
].join(" ");

export interface IndexCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Push a pin through the top of the card: this one is not going in the box
   * yet. Pins belong on cork - do not pin cards lying on a desk or a page.
   */
  pinned?: boolean;
  /** Written larger on the first rule: what this card is about. */
  header?: React.ReactNode;
  /** The card's single job, written on the rules. */
  children: React.ReactNode;
  /**
   * A full-width strip flush to the card's bottom edge, below the ruled area,
   * separated by a stronger rule. This is a slot, not a control: pass your own
   * interactive element (e.g. a button carrying aria-expanded) and the band
   * itself stays inert.
   */
  band?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * A ruled index card: one card, one job. Tasks and notifications are index
 * cards - a thing to do, written down, handed over, thrown out when done. If
 * something needs two cards, it is two jobs.
 *
 * The ruling is uniform from the top of the writing area down; the header is
 * not a separate band, it is simply written larger on the first rule. The
 * period is the --ruling custom property on the writing area (default 1.6em):
 * any content that sets its own line-height must set it to var(--ruling).
 * The optional band sits under the rules, outside the ruled area.
 *
 * Writing is the strongest ink (text-ink) by default, because what a person
 * wrote on the card is the thing to read. Lines that are annotation rather
 * than writing - a filing line, a caption - say so with text-annotation.
 */
export function IndexCard({
  pinned = false,
  header,
  band,
  children,
  className,
  ...rest
}: IndexCardProps) {
  return (
    <div
      className={cn(
        "relative flex flex-col bg-card border border-rule rounded-paper shadow-lift",
        className,
      )}
      {...rest}
    >
      {pinned ? <Pin /> : null}
      <div
        className={cn(
          RULING,
          "grow p-4 font-body text-[0.9375rem] leading-[var(--ruling)] text-ink",
        )}
      >
        {header ? (
          // Inherits the computed 24px line-height, so the bigger type still
          // occupies exactly one rule - it fills the line rather than
          // crossing it, which is what a heading written by hand does.
          <div className="font-display text-[1.125rem] font-medium text-ink">
            {header}
          </div>
        ) : null}
        {children}
      </div>
      {band ? (
        // Inner radius = card radius minus the 1px border, so the recess fill
        // meets the card's rounded corners without poking past them. No
        // overflow clipping anywhere: a lifted or recolored child must be free
        // to cast its shadow outside the card.
        <div className="rounded-b-[calc(var(--radius-paper)-1px)] border-t border-rule bg-recess p-1">
          {band}
        </div>
      ) : null}
    </div>
  );
}
